/**
 * Reward config resolution (R7.9, ADR-0002 "Defaults live in code, not
 * seeded events"): derived config is `DEFAULT_REWARD_CONFIG` overridden by
 * the LATEST `reward_config.changed` event, if any, applied to the ENTIRE
 * history (not config-at-time-of-event — ADR-0002's explicit ruling,
 * restated in the reward-engine readiness audit addendum: "the latest
 * config applies to the entire history"). Pure, no I/O (ADR-0001 rule 2).
 */

import type { CompassEvent, RewardConfig } from '../../contracts';
import { DEFAULT_REWARD_CONFIG } from '../../contracts';
import { eventsOfTypes, sortByEventId } from '../event-log/event-log';

/**
 * The config in effect right now, given the full event log. `reward_config
 * .changed` events are never revertible (not in `VALID_REVERT_TARGET_TYPES`)
 * so no revert-filtering is needed here — every recorded snapshot is live
 * history; only the LAST one (canonical eventId order) matters for
 * "current" derivation. Earlier snapshots stay in the log for audit/export
 * but are never consulted once a later one exists.
 *
 * Callers normally already pass a canonically-sorted log (`EventCachePort
 * .allEvents()` guarantees this — `ports.ts`), but this function sorts
 * defensively rather than trusting caller order, since "latest" is only
 * meaningful under canonical (lexicographic eventId) order, never
 * array-append order (which can differ after a sync merge).
 */
export function resolveRewardConfig(
  events: readonly CompassEvent[],
): RewardConfig {
  const changes = eventsOfTypes(sortByEventId(events), ['reward_config.changed']);
  const latest = changes[changes.length - 1];
  return latest ? latest.config : DEFAULT_REWARD_CONFIG;
}
